/** Tableau catalogue : libellé, unité, HT, TVA, création, actions. */
import { useFormatters } from "../../formats/useFormatters";
import { ListPager, usePagedRows } from "../../components/ListPager";
import { SortTh, sortRows, useTableSort } from "../../components/tableSort";
import "../clients/tables.css";
import { CatalogActions } from "./CatalogActions";
import { pcgFull } from "../../pcg/pcg";
import { excerpt, isActive, taxLabel } from "./format";
import { opKindLabel } from "../../legal/opNature";
import type { Product, TaxRate } from "./types";

type CatalogTableProps = {
  rows: Product[];
  rates: TaxRate[];
  emptyText?: string;
  menuId: number | null;
  onMenu: (id: number | null) => void;
  onOpen: (id: number) => void;
  onEdit: (id: number) => void;
  onDuplicate: (id: number) => void;
  onToggleActive: (row: Product) => void;
  onPurge: (row: Product) => void;
  onCreateQuote?: (productId: number) => void;
  onCreateInvoice?: (productId: number) => void;
};

export function CatalogTable({
  rows,
  rates,
  emptyText = "Aucun produit.",
  menuId,
  onMenu,
  onOpen,
  onEdit,
  onDuplicate,
  onToggleActive,
  onPurge,
  onCreateQuote,
  onCreateInvoice,
}: CatalogTableProps) {
  const { money, day } = useFormatters();
  const sort = useTableSort<"label" | "kind" | "unit" | "price" | "tax" | "created">();
  const shown = sort.key
    ? sortRows(rows, sort.dir, sort.key === "created" ? "date" : sort.key === "price" ? "num" : "text", (p) => {
        if (sort.key === "label") return p.label || "";
        if (sort.key === "kind") return opKindLabel(p.op_kind);
        if (sort.key === "unit") return p.unit || "u";
        if (sort.key === "price") return p.unit_price_ht_cents;
        if (sort.key === "tax") return taxLabel(rates, p.tax_rate_id);
        return p.created_at || "";
      })
    : rows;
  const paged = usePagedRows(shown);
  if (rows.length === 0) {
    return (
      <div className="fx-clients-wrap">
        <p className="fx-clients-empty">{emptyText}</p>
      </div>
    );
  }
  return (
    <div className="fx-clients-wrap">
      <table className="fx-clients-table">
        <thead>
          <tr>
            <SortTh label="Libellé" active={sort.key === "label"} dir={sort.dir} onSort={() => sort.onSort("label", "text")} />
            <SortTh label="Nature" active={sort.key === "kind"} dir={sort.dir} onSort={() => sort.onSort("kind", "text")} />
            <SortTh label="Unité" active={sort.key === "unit"} dir={sort.dir} onSort={() => sort.onSort("unit", "text")} />
            <SortTh className="is-num" label="Prix HT" active={sort.key === "price"} dir={sort.dir} onSort={() => sort.onSort("price", "num")} />
            <SortTh label="TVA" active={sort.key === "tax"} dir={sort.dir} onSort={() => sort.onSort("tax", "text")} />
            <SortTh label="Créé le" active={sort.key === "created"} dir={sort.dir} onSort={() => sort.onSort("created", "date")} />
            <th className="is-act" scope="col">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {paged.slice.map((p) => {
            const on = isActive(p);
            return (
              <tr
                key={p.id}
                className={`fx-clients-table__row${on ? "" : " is-archived"}`}
                onClick={() => onOpen(p.id)}
              >
                <td>
                  <span className="fx-clients-table__id">
                    <span className="fx-clients-table__name">{p.label}</span>
                    {p.product_code ? <span className="fx-clients-table__tag">{p.product_code}</span> : null}
                    {p.pcg_code ? <span className="fx-clients-table__tag">{pcgFull(p.pcg_code, p.pcg_sub || "")}</span> : null}
                    {on ? null : <span className="fx-clients-table__tag">Inactif</span>}
                  </span>
                  {excerpt(p.description) ? (
                    <span className="fx-clients-table__meta">{excerpt(p.description)}</span>
                  ) : null}
                </td>
                <td className="fx-clients-table__muted">{opKindLabel(p.op_kind)}</td>
                <td className="fx-clients-table__muted">{p.unit || "u"}</td>
                <td className="is-num">{money(p.unit_price_ht_cents)}</td>
                <td className="fx-clients-table__muted">{taxLabel(rates, p.tax_rate_id)}</td>
                <td className="fx-clients-table__muted">{day(p.created_at)}</td>
                <td className="is-act" onClick={(e) => e.stopPropagation()}>
                  <CatalogActions
                    product={p}
                    open={menuId === p.id}
                    onToggle={() => onMenu(menuId === p.id ? null : p.id)}
                    onClose={() => onMenu(null)}
                    onEdit={() => {
                      onMenu(null);
                      onEdit(p.id);
                    }}
                    onDuplicate={() => {
                      onMenu(null);
                      onDuplicate(p.id);
                    }}
                    onToggleActive={() => {
                      onMenu(null);
                      onToggleActive(p);
                    }}
                    onPurge={() => {
                      onMenu(null);
                      onPurge(p);
                    }}
                    onCreateQuote={
                      onCreateQuote
                        ? () => {
                            onMenu(null);
                            onCreateQuote(p.id);
                          }
                        : undefined
                    }
                    onCreateInvoice={
                      onCreateInvoice
                        ? () => {
                            onMenu(null);
                            onCreateInvoice(p.id);
                          }
                        : undefined
                    }
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <ListPager page={paged.page} pages={paged.pages} total={paged.total} from={paged.from} to={paged.to} setPage={paged.setPage} />
    </div>
  );
}
